import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  Calendar, Plus, Clock, User, Mail, Phone,
  Check, Loader2, ExternalLink, Copy, Link, Search, X,
  Pencil, Trash2, Save, XCircle
} from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  booked: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  canceled: 'bg-red-500/15 text-red-400 border-red-500/20',
  completed: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  no_show: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
};

const SOURCE_BADGE = {
  manual: { label: 'Manual', className: 'bg-gray-500/15 text-gray-400 border-gray-500/20' },
  phone_call: { label: 'Phone Call', className: 'bg-amber-500/15 text-amber-400 border-amber-500/20' },
  chat_widget: { label: 'Chat', className: 'bg-purple-500/15 text-purple-400 border-purple-500/20' },
  calendly: { label: 'Calendly', className: 'bg-blue-500/15 text-blue-400 border-blue-500/20' },
};

const Appointments = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const headers = { Authorization: `Bearer ${token}` };

  const [appointments, setAppointments] = useState([]);
  const [bookingUrl, setBookingUrl] = useState('');
  const [calendlyStatus, setCalendlyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [form, setForm] = useState({ name: '', email: '', phone: '', scheduled_time: '', notes: '', availability: {} });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [cancelingId, setCancelingId] = useState(null);
  const [userAvailability, setUserAvailability] = useState({});
  const [savingAvail, setSavingAvail] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [apptsRes, statusRes, availRes] = await Promise.allSettled([
        axios.get(`${API}/calendly/appointments`, { headers }),
        axios.get(`${API}/calendly/status`, { headers }),
        axios.get(`${API}/calendly/availability`, { headers }),
      ]);
      if (apptsRes.status === 'fulfilled') {
        setAppointments(apptsRes.value.data.appointments || []);
        setBookingUrl(apptsRes.value.data.booking_url || '');
        setLinkInput(apptsRes.value.data.booking_url || '');
      }
      if (statusRes.status === 'fulfilled') setCalendlyStatus(statusRes.value.data);
      if (availRes.status === 'fulfilled') setUserAvailability(availRes.value.data.availability || {});
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveAvailability = async () => {
    setSavingAvail(true);
    try {
      await axios.put(`${API}/calendly/availability`, { availability: userAvailability }, { headers });
      toast({ title: 'Availability saved', description: 'Your AI receptionist will use this schedule when booking.' });
    } catch {
      toast({ title: 'Failed to save availability', variant: 'destructive' });
    }
    setSavingAvail(false);
  };

  const saveLink = async () => {
    if (!linkInput.trim()) return;
    setSavingLink(true);
    try {
      await axios.post(`${API}/calendly/set-link`, { calendly_url: linkInput.trim() }, { headers });
      toast({ title: 'Booking link saved' });
      setBookingUrl(linkInput.trim());
      fetchAll();
    } catch (err) {
      toast({ title: 'Failed to save', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setSavingLink(false); }
  };

  const createAppointment = async () => {
    if (!form.name.trim() || !form.scheduled_time) {
      toast({ title: 'Name and time are required', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/calendly/appointments`, form, { headers });
      toast({ title: 'Appointment created' });
      setForm({ name: '', email: '', phone: '', scheduled_time: '', notes: '', availability: {} });
      setShowForm(false);
      fetchAll();
    } catch (err) {
      toast({ title: 'Failed to create', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const startEdit = (appt) => {
    setEditingId(appt.id);
    setEditForm({
      name: appt.invitee_name || '',
      email: appt.invitee_email || '',
      phone: appt.invitee_phone || '',
      scheduled_time: appt.scheduled_time ? appt.scheduled_time.slice(0, 16) : '',
      notes: appt.notes || '',
    });
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await axios.put(`${API}/calendly/appointments/${editingId}`, editForm, { headers });
      toast({ title: 'Appointment updated' });
      setEditingId(null);
      fetchAll();
    } catch (err) {
      toast({ title: 'Failed to update', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setSavingEdit(false); }
  };

  const cancelAppointment = async (apptId) => {
    setCancelingId(apptId);
    try {
      await axios.delete(`${API}/calendly/appointments/${apptId}`, { headers });
      toast({ title: 'Appointment canceled' });
      fetchAll();
    } catch (err) {
      toast({ title: 'Failed to cancel', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setCancelingId(null); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    toast({ title: 'Booking link copied' });
  };

  const filteredAppts = appointments.filter(a => {
    const matchesSearch = !searchTerm ||
      (a.invitee_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.invitee_email || '').toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (activeFilter === 'booked') return a.status === 'booked';
    if (activeFilter === 'canceled') return a.status === 'canceled';
    return true;
  });

  const bookedCount = appointments.filter(a => a.status === 'booked').length;
  const canceledCount = appointments.filter(a => a.status === 'canceled').length;

  const filterTabs = [
    { key: 'all', label: 'All', count: appointments.length },
    { key: 'booked', label: 'Booked', count: bookedCount },
    { key: 'canceled', label: 'Cancelled', count: canceledCount },
  ];

  return (
    <div className="min-h-screen bg-[#0a1628] text-white" data-testid="appointments-page">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold" data-testid="appointments-title">Appointments</h1>
              <p className="text-gray-500 text-sm">Manage bookings and your scheduling link</p>
            </div>
          </div>
          <Button onClick={() => setShowForm(!showForm)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-appointment-btn">
            <Plus className="w-4 h-4 mr-2" /> Log Appointment
          </Button>
        </div>

        {/* Booking Link Setup */}
        <Card className="bg-white/[0.03] border-white/[0.06] mb-6" data-testid="booking-link-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Link className="w-4 h-4 text-purple-400" /> Your Booking Link
            </CardTitle>
            <CardDescription className="text-gray-400">
              Paste your Calendly, Google Calendar, or Cal.com booking link. Your AI agent will share this with callers and chat visitors who want to schedule.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={linkInput}
                onChange={e => setLinkInput(e.target.value)}
                placeholder="https://calendly.com/your-name/30min or https://calendar.app.google/..."
                className="bg-black/30 border-white/10 text-white flex-1"
                data-testid="calendly-url-input"
              />
              <Button onClick={saveLink} disabled={savingLink} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-link-btn">
                {savingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </Button>
            </div>
            {bookingUrl && (
              <div className="flex items-center gap-3 mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/20 flex-shrink-0" data-testid="booking-provider-badge">
                  {bookingUrl.includes('calendar.app.google') || bookingUrl.includes('calendar.google.com') ? 'Google Calendar' : bookingUrl.includes('cal.com') ? 'Cal.com' : 'Calendly'}
                </span>
                <p className="text-sm text-emerald-300 flex-1 truncate">{bookingUrl}</p>
                <button onClick={copyLink} className="text-gray-400 hover:text-white" data-testid="copy-link-btn"><Copy className="w-4 h-4" /></button>
                <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white" data-testid="open-link-btn"><ExternalLink className="w-4 h-4" /></a>
              </div>
            )}
            {calendlyStatus?.connected && (
              <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                <Check className="w-3 h-3" /> Calendly OAuth connected: {calendlyStatus.calendly_name}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Availability Schedule */}
        <Card className="bg-white/[0.03] border-white/[0.06] mb-6" data-testid="availability-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white text-base flex items-center gap-2"><Clock className="w-4 h-4 text-blue-400" /> Your Availability</CardTitle>
                <CardDescription className="text-gray-500 text-xs mt-1">Set your available hours. Mia will only book appointments within these times.</CardDescription>
              </div>
              <Button size="sm" onClick={saveAvailability} disabled={savingAvail} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="save-availability-btn">
                {savingAvail ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="text-center">
                  <span className="text-xs font-medium text-gray-400 block mb-1.5">{day}</span>
                  <Input
                    type="text"
                    value={userAvailability[day] || ''}
                    onChange={e => setUserAvailability(prev => ({ ...prev, [day]: e.target.value }))}
                    className="bg-black/30 border-white/10 text-white text-xs h-8 text-center px-1"
                    placeholder="9-5"
                    data-testid={`avail-${day.toLowerCase()}`}
                  />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-600 mt-2">Enter time range (e.g. 9:00-17:00). Leave blank = closed that day.</p>
          </CardContent>
        </Card>

        {/* Manual Appointment Form */}
        {showForm && (
          <Card className="bg-white/[0.03] border-white/[0.06] mb-6" data-testid="new-appointment-form">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base">Log New Appointment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300 text-sm">Name *</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1" data-testid="appt-name" placeholder="John Smith" />
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">Date & Time *</Label>
                  <Input type="datetime-local" value={form.scheduled_time} onChange={e => setForm(p => ({ ...p, scheduled_time: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1" data-testid="appt-time" />
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">Email</Label>
                  <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1" data-testid="appt-email" placeholder="john@example.com" />
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">Phone</Label>
                  <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1" data-testid="appt-phone" placeholder="+1 555-1234" />
                </div>
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Notes</Label>
                <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1" data-testid="appt-notes" placeholder="Any additional notes..." />
              </div>
              <div className="flex gap-2">
                <Button onClick={createAppointment} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-appt-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Create
                </Button>
                <Button variant="ghost" onClick={() => setShowForm(false)} className="text-gray-400"><X className="w-4 h-4 mr-1" /> Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search + Filter Tabs */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
          <div className="flex gap-1 bg-white/[0.03] rounded-lg p-1" data-testid="appointment-filter-tabs">
            {filterTabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveFilter(tab.key)} data-testid={`filter-${tab.key}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeFilter === tab.key ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeFilter === tab.key ? 'bg-white/20' : 'bg-white/5'}`}>{tab.count}</span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search appointments..." className="pl-10 bg-white/[0.03] border-white/[0.06] text-white" data-testid="search-appointments" />
          </div>
        </div>

        {/* Appointments List */}
        <div data-testid="appointments-list">
          {loading ? (
            <p className="text-gray-500 text-sm text-center py-8">Loading...</p>
          ) : filteredAppts.length === 0 ? (
            <Card className="bg-white/[0.03] border-white/[0.06]">
              <CardContent className="py-16 text-center">
                <Calendar className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <h3 className="text-base font-semibold text-gray-400 mb-1">
                  {activeFilter === 'canceled' ? 'No cancelled appointments' : activeFilter === 'booked' ? 'No booked appointments' : 'No appointments yet'}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {activeFilter === 'all' && !bookingUrl
                    ? 'Add your Calendly URL above so your AI agent can book appointments for you.'
                    : activeFilter === 'all'
                    ? 'Your AI agent will share your booking link with callers and chat visitors.'
                    : `No appointments with "${activeFilter}" status found.`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredAppts.map((appt, i) => (
                <AppointmentCard
                  key={appt.id || appt.calendly_event_uri || i}
                  appt={appt}
                  editingId={editingId}
                  editForm={editForm}
                  setEditForm={setEditForm}
                  onEdit={startEdit}
                  onSaveEdit={saveEdit}
                  onCancelEdit={() => setEditingId(null)}
                  onCancel={cancelAppointment}
                  savingEdit={savingEdit}
                  cancelingId={cancelingId}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AppointmentCard = ({ appt, editingId, editForm, setEditForm, onEdit, onSaveEdit, onCancelEdit, onCancel, savingEdit, cancelingId }) => {
  const status = STATUS_BADGE[appt.status] || STATUS_BADGE.booked;
  const time = appt.scheduled_time ? new Date(appt.scheduled_time) : null;
  const isUpcoming = appt.status === 'booked' && time && time > new Date();
  const isEditing = editingId === appt.id;
  const isCanceling = cancelingId === appt.id;
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  if (isEditing) {
    return (
      <Card className="bg-white/[0.03] border-blue-500/30 border" data-testid={`appointment-edit-${appt.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Pencil className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-blue-400">Editing Appointment</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-400 text-xs">Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1 h-9" data-testid="edit-appt-name" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Date & Time</Label>
              <Input type="datetime-local" value={editForm.scheduled_time} onChange={e => setEditForm(p => ({ ...p, scheduled_time: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1 h-9" data-testid="edit-appt-time" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Email</Label>
              <Input value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1 h-9" data-testid="edit-appt-email" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Phone</Label>
              <Input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1 h-9" data-testid="edit-appt-phone" />
            </div>
          </div>
          <div>
            <Label className="text-gray-400 text-xs">Notes</Label>
            <Textarea value={editForm.notes} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1 min-h-[60px]" data-testid="edit-appt-notes" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={onSaveEdit} disabled={savingEdit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-edit-btn">
              {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancelEdit} className="text-gray-400" data-testid="cancel-edit-btn">
              <X className="w-3.5 h-3.5 mr-1" /> Discard
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`bg-white/[0.03] border-white/[0.06] hover:border-white/[0.12] transition-colors ${isUpcoming ? 'border-l-2 border-l-blue-500' : ''}`} data-testid={`appointment-${appt.id || 'item'}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isUpcoming ? 'bg-blue-600/20 text-blue-400' : appt.status === 'canceled' ? 'bg-red-600/20 text-red-400' : 'bg-gray-600/20 text-gray-400'}`}>
          {appt.status === 'canceled' ? <XCircle className="w-5 h-5" /> : <Calendar className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-white truncate">{appt.invitee_name || 'Unknown'}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${status}`}>{appt.status}</span>
            {(() => {
              const src = appt.source || (appt.calendly_event_uri ? 'calendly' : '');
              const badge = SOURCE_BADGE[src];
              return badge ? <span className={`text-[10px] px-2 py-0.5 rounded-full border ${badge.className}`} data-testid={`source-badge-${src}`}>{badge.label}</span> : null;
            })()}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
            {time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{time.toLocaleString()}</span>}
            {appt.invitee_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{appt.invitee_email}</span>}
            {appt.invitee_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{appt.invitee_phone}</span>}
          </div>
          {appt.notes && <p className="text-xs text-gray-600 mt-1 truncate">{appt.notes}</p>}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {appt.status !== 'canceled' && (
            <>
              <Button size="sm" variant="ghost" onClick={() => onEdit(appt)} className="text-gray-400 hover:text-blue-400 h-8 w-8 p-0" data-testid={`edit-btn-${appt.id}`} title="Edit">
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              {showCancelConfirm ? (
                <div className="flex items-center gap-1 bg-red-500/10 rounded-lg px-2 py-1">
                  <span className="text-xs text-red-400">Cancel?</span>
                  <Button size="sm" variant="ghost" onClick={() => { onCancel(appt.id); setShowCancelConfirm(false); }} disabled={isCanceling} className="text-red-400 hover:text-red-300 h-6 px-2" data-testid={`confirm-cancel-${appt.id}`}>
                    {isCanceling ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowCancelConfirm(false)} className="text-gray-400 h-6 px-2" data-testid={`deny-cancel-${appt.id}`}>No</Button>
                </div>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setShowCancelConfirm(true)} className="text-gray-400 hover:text-red-400 h-8 w-8 p-0" data-testid={`cancel-btn-${appt.id}`} title="Cancel appointment">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default Appointments;
